import { useState, useRef } from "react";
import { useScrollTabs } from "hooks";
import { motion, AnimatePresence } from "framer-motion";
import { FourArrows } from "assets/website";
import {
  insightsDomainKnowledge,
  insightsPersonality,
  insightsSoftSkills,
  insightsWorkMotivators,
  insightsWorkStyle,
  insightsWorkplaceQualities,
} from "assets/images";
import { SampleReportModal } from "../../SampleReportModal";
import { ReactComponent as DomainKnowledgeLine1 } from "assets/icons/lines/domain-knowledge-line1.svg";
import { ReactComponent as DomainKnowledgeLine2 } from "assets/icons/lines/domain-knowledge-line2.svg";
import { ReactComponent as SoftSkillsLine1 } from "assets/icons/lines/soft-skills-line1.svg";
import { ReactComponent as WorkMotivators1 } from "assets/icons/lines/work-motivators-line1.svg";
import { ReactComponent as PersonalityLine1 } from "assets/icons/lines/personality-line1.svg";
import { ReactComponent as PersonalityLine2 } from "assets/icons/lines/personality-line2.svg";
import { ReactComponent as PersonalityLine3 } from "assets/icons/lines/personality-line3.svg";
import { ReactComponent as PersonalityLine4 } from "assets/icons/lines/personality-line4.svg";
import { ReactComponent as WorkStyleLine1 } from "assets/icons/lines/work-style-line1.svg";
import { ReactComponent as WorkStyleLine2 } from "assets/icons/lines/work-style-line2.svg";
import { ReactComponent as WorkplaceQualitiesLine1 } from "assets/icons/lines/workplace-qualities-line1.svg";

interface DimensionConfig {
  label: string;
  heading: string;
  body: string;
  image: string;
}

const dimensions: DimensionConfig[] = [
  {
    label: "Domain Knowledge",
    heading: "Go beyond what they claim to know",
    body: "Understand how well candidates apply their knowledge in real work scenarios not just what their CV suggests.",
    image: insightsDomainKnowledge,
  },
  {
    label: "Soft Skills",
    heading: "See how they actually work with people",
    body: "Evaluate communication, collaboration, and relationship dynamics before they enter your team.",
    image: insightsSoftSkills,
  },
  {
    label: "Work Motivators",
    heading: "Know what truly drives performance",
    body: "Identify what sustains a candidate’s motivation and what may quietly limit it over time.",
    image: insightsWorkMotivators,
  },
  {
    label: "Personality",
    heading: "Know how they’re likely to show up",
    body: "Gain insight into natural patterns in thinking, decision-making, and interaction at work.",
    image: insightsPersonality,
  },
  {
    label: "Work Style",
    heading: "Understand how work gets done",
    body: "See how candidates plan, prioritize, and deliver especially under pressure and competing demands.",
    image: insightsWorkStyle,
  },
  {
    label: "Workplace Qualities",
    heading: "Match people to the reality of your workplace",
    body: "Assess how well a candidate’s preferred environment aligns with how your team actually operates.",
    image: insightsWorkplaceQualities,
  },
];

const InsightsSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [showSampleReportModal, setShowSampleReportModal] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  const active = dimensions[activeIndex];

  // Scrolling or swiping over the panel steps through the dimensions.
  useScrollTabs(sectionRef, activeIndex, dimensions.length, setActiveIndex);

  return (
    <section className="py-16 md:py-24 bg-[#EAECF0]">
      <div className="container mx-auto xl:px-8 lg:px-4 px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14">
          <div
            data-aos="fade-up"
            className="inline-flex py-2 items-center gap-2 mb-4 relative before:content-[''] before:absolute before:top-0 before:left-0 before:right-0 before:h-[1px] before:bg-[linear-gradient(90deg,#C6E0FF_0%,#000B55_100%)] after:content-[''] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[1px] after:bg-[linear-gradient(90deg,#C6E0FF_0%,#000B55_100%)]"
          >
            <FourArrows />
            <span className="sm:text-lg text-base text-[#050B17] tracking-wider">
              Insights
            </span>
          </div>
          <h2
            data-aos="fade-up"
            data-aos-delay="100"
            className="text-2xl md:text-4xl lg:text-5xl font-display font-bold text-[#050B17]"
          >
            Understand Each Talent Across 6 Core Dimensions
          </h2>
        </div>

        {/* Tab Bar */}
        <div
          data-aos="fade-up"
          data-aos-delay="200"
          className="flex overflow-x-auto scrollbar-none gap-1 border-b border-[#EAECF0] mb-0"
        >
          {dimensions.map((dim, index) => (
            <button
              key={dim.label}
              onClick={() => setActiveIndex(index)}
              className={`mx-3 md:mx-5 py-3 md:py-4 text-sm md:text-base lg:text-lg xl:text-xl whitespace-nowrap flex-shrink-0 transition-all duration-200 border-b-2 ${activeIndex === index
                ? "text-[#012169] border-[#FC9F4C] font-medium"
                : "text-[#344054] border-transparent hover:text-[#344054]"
                }`}
            >
              {dim.label}
            </button>
          ))}
        </div>

        {/* Content Panel */}
        <div
          ref={sectionRef}
          data-aos="fade-up"
          data-aos-delay="300"
          className="bg-white rounded-lg py-6 sm:px-5 px-4 overflow-hidden"
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={`lines-${activeIndex}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              // Desktop is untouched. On phones the full-size lines sprawl across
              // the heading, so the group is scaled down into the panel's top-left
              // corner — the corner the lines are already anchored to — and each
              // line faded back. Scaling the group (rather than each SVG) keeps
              // the multi-line tabs' composition intact, and the fade sits on the
              // children because framer-motion owns the inline opacity here.
              className="absolute inset-0 pointer-events-none max-sm:origin-top-left max-sm:scale-[0.45] max-sm:[&>*]:opacity-70"
            >
              {activeIndex === 0 && (
                <>
                  <DomainKnowledgeLine1 className="absolute top-0 left-0" />
                  <DomainKnowledgeLine2 className="absolute top-0 left-0" />
                </>
              )}
              {activeIndex === 1 && <SoftSkillsLine1 className="absolute top-0 left-0" />}
              {activeIndex === 2 && <WorkMotivators1 className="absolute top-0 left-0" />}
              {activeIndex === 3 && (
                <>
                  <PersonalityLine1 className="absolute top-0 left-6" />
                  <PersonalityLine2 className="absolute top-0 left-0" />
                  <PersonalityLine3 className="absolute top-0 left-0" />
                  <PersonalityLine4 className="absolute top-0 left-0" />
                </>
              )}
              {activeIndex === 4 && (
                <>
                  <WorkStyleLine1 className="absolute top-0 left-14" />
                  <WorkStyleLine2 className="absolute top-0 left-0" />
                </>
              )}
              {activeIndex === 5 && <WorkplaceQualitiesLine1 className="absolute top-0 left-0" />}
            </motion.div>
          </AnimatePresence>
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-0 max-sm:gap-8 items-stretch">
            {/* Left: text + decorative lines */}
            <div className="relative sm:pl-4 flex flex-col justify-center gap-4">
              <AnimatePresence mode="wait">
                <motion.div
                  key={`text-${activeIndex}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.35, ease: "easeInOut" }}
                  className="flex flex-col sm:gap-4 gap-2"
                >
                  <h3 className="text-base md:text-2xl lg:text-3xl sm:text-xl font-bold text-[#050B17]">
                    {active.heading}
                  </h3>
                  <p className="text-sm md:text-base text-[#475467] leading-relaxed max-w-[360px]">
                    {active.body}
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Right: screenshot — all images stay mounted so switching never
                triggers a re-fetch; the animation is driven by activeIndex
                instead of mounting/unmounting, so the crossfade is preserved. */}
            <div className="relative flex items-center justify-center">
              {dimensions.map((dim, index) => (
                <motion.img
                  key={dim.label}
                  src={dim.image}
                  alt={dim.label}
                  aria-hidden={index !== activeIndex}
                  initial={false}
                  animate={{
                    opacity: index === activeIndex ? 1 : 0,
                    scale: index === activeIndex ? 1 : 1.03,
                  }}
                  transition={{ duration: 0.4, ease: "easeInOut" }}
                  className={`rounded-xl object-cover ${
                    index === 0
                      ? "relative w-full h-auto"
                      : "absolute inset-0 w-full h-full"
                  } ${index === activeIndex ? "" : "pointer-events-none"}`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Sample Report CTA */}
        <div className="max-w-[1024px] mx-auto px-6 text-center mt-12 md:mt-16">
          <h2
            data-aos="fade-up"
            className="text-xl md:text-2xl lg:text-3xl font-display font-bold text-[#050B17] mb-4"
          >
            See What a Full Precision Report Looks Like
          </h2>
          <p
            data-aos="fade-up"
            data-aos-delay="100"
            className="text-base md:text-lg text-[#475467] max-w-3xl mx-auto mb-8"
          >
            A full Precision report gives you a complete, multi-dimensional view
            of every candidate, so you can understand not just who they are, but
            how they&rsquo;ll perform in the role.
          </p>
          <button
            data-aos="fade-up"
            data-aos-delay="200"
            onClick={() => setShowSampleReportModal(true)}
            className="px-8 py-4 bg-[#012169] hover:bg-[#01154d] text-base text-white font-semibold rounded-lg shadow-[0px_4px_8px_0px_#01216940] transition-all duration-200 hover:shadow-lg"
          >
            View a Full Sample Report
          </button>
        </div>
      </div>

      <SampleReportModal
        show={showSampleReportModal}
        onClose={() => setShowSampleReportModal(false)}
      />
    </section>
  );
};

export { InsightsSection };
